import { Link } from 'react-router'
import { Eyebrow, GoldRule } from '#app/components/marketing/index.ts'
import { type Route } from './+types/privacy.ts'

export const meta: Route.MetaFunction = () => [
	{ title: 'Privacy Policy · Frame & Ledger' },
	{
		name: 'description',
		content:
			'How Frame & Ledger LLC collects, uses, discloses, and safeguards personal information at frameandledger.com.',
	},
]

const EFFECTIVE_DATE = 'June 8, 2026'
const LAST_UPDATED = 'June 8, 2026'

export default function PrivacyRoute() {
	return (
		<main className="container mx-auto px-4 pt-16 pb-24 lg:px-6 lg:pt-24">
			<header className="mx-auto max-w-3xl">
				<Eyebrow>Legal · Privacy Policy</Eyebrow>
				<h1 className="mt-4 font-display text-4xl font-medium tracking-tight text-foreground sm:text-5xl">
					Privacy Policy
				</h1>
				<GoldRule className="mt-6" />
				<dl className="mt-6 grid grid-cols-1 gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground sm:grid-cols-2">
					<div>
						<dt className="inline">Effective:&nbsp;</dt>
						<dd className="inline text-foreground">{EFFECTIVE_DATE}</dd>
					</div>
					<div>
						<dt className="inline">Last updated:&nbsp;</dt>
						<dd className="inline text-foreground">{LAST_UPDATED}</dd>
					</div>
				</dl>
			</header>

			<article className="mx-auto mt-12 max-w-3xl space-y-6 text-base/7 text-foreground">
				<p>
					Frame &amp; Ledger LLC (<strong>“Frame &amp; Ledger,” “F&amp;L,”
					“we,” “us,”</strong> or <strong>“our”</strong>) is a Florida limited
					liability company that designs and licenses Shopify infrastructure for
					hybrid (B2B2C) custom manufacturers. This Privacy Policy describes how
					we collect, use, disclose, and safeguard personal information when you
					visit <strong>frameandledger.com</strong> and any related subdomains
					(the <strong>“Website”</strong>), submit an inquiry, communicate with
					us, or engage F&amp;L for professional services.
				</p>
				<p>
					This Policy applies to information collected through the Website and
					through our business operations. It does <strong>not</strong> apply to
					information you provide to third-party platforms (for example,
					Shopify, Klaviyo, HubSpot, your accounting system, or your ad
					networks), even where F&amp;L operates inside those platforms on your
					behalf. Your relationship with those platforms is governed by their
					own terms and privacy notices.
				</p>

				<Section id="who-we-are" num="1" title="Who We Are">
					<p>
						Frame &amp; Ledger LLC is the entity responsible for the personal
						information processed under this Policy.
					</p>
					<ul className="ml-6 list-disc space-y-1.5">
						<li>
							<strong>Entity:</strong> Frame &amp; Ledger LLC
						</li>
						<li>
							<strong>Formation:</strong> Florida limited liability company
						</li>
						<li>
							<strong>Website:</strong>{' '}
							<a
								href="https://frameandledger.com"
								className="text-gold underline hover:no-underline"
							>
								https://frameandledger.com
							</a>
						</li>
						<li>
							<strong>Primary contact for privacy inquiries:</strong>{' '}
							<a
								href="mailto:privacy@frameandledger.com"
								className="text-gold underline hover:no-underline"
							>
								privacy@frameandledger.com
							</a>
						</li>
						<li>
							<strong>Founders &amp; principals:</strong> Adam Wells (CEO),
							Jonathan Wells (CTO)
						</li>
					</ul>
				</Section>

				<Section id="scope" num="2" title="Scope and Audience">
					<p>
						The Website is directed at owners, founders, executives, and
						operators of business entities — not at consumers, and not at
						children. By using the Website, you represent that you are at least
						eighteen (18) years of age and are visiting in connection with a
						business purpose, including evaluating Frame &amp; Ledger as a
						service provider.
					</p>
					<p>
						Frame &amp; Ledger is a business-to-business firm. We do not sell
						consumer products through the Website, and we do not build
						behavioral profiles of consumers for advertising purposes. We do
						advertise our own services on third-party platforms, and we measure
						which of those advertisements lead to inquiries.
					</p>
				</Section>

				<Section id="information-we-collect" num="3" title="Information We Collect">
					<p>
						We collect personal information in three ways: (a) information you
						provide to us directly, (b) information we collect automatically
						when you use the Website, and (c) information we receive from
						clients in the course of delivering professional services.
					</p>

					<SubHeading>3.1 Information You Provide Directly</SubHeading>
					<p>
						When you complete the <strong>Request Consultation</strong> form,
						send us an email, schedule a discovery call, sign an engagement
						document, or otherwise interact with us, you may provide:
					</p>
					<ul className="ml-6 list-disc space-y-1.5">
						<li>Your full name, business email address, and role or title;</li>
						<li>The name, website, and general description of the business you represent;</li>
						<li>Information you share about your operations, goals, current technology stack, sales motion, financials, or challenges, including any documents or screenshots you attach;</li>
						<li>Calendaring and scheduling information necessary to coordinate calls;</li>
						<li>Signature, billing, and entity-formation information required to execute and administer an engagement agreement (for example, a Master Service Agreement, Application License Agreement, Statement of Work, or Ad Pricing Schedule).</li>
					</ul>

					<SubHeading>3.2 Information Collected Automatically</SubHeading>
					<p>
						When you visit the Website, we and certain trusted service providers
						may collect technical information automatically, including:
					</p>
					<ul className="ml-6 list-disc space-y-1.5">
						<li>IP address, approximate location derived from IP, and time zone;</li>
						<li>Browser type, operating system, device type, and screen characteristics;</li>
						<li>Pages viewed, links clicked, referring URL, and session duration;</li>
						<li>Cookie and similar identifiers (see Section 7);</li>
						<li>Server log data necessary to operate, secure, and improve the Website.</li>
					</ul>
					<p>
						We use this information for analytics, security, debugging,
						product improvement, and measuring the performance of our own
						advertising. We do not use it to build cross-site advertising
						profiles.
					</p>

					<SubHeading>3.3 Information Received Through Client Engagements</SubHeading>
					<p>
						If your organization engages Frame &amp; Ledger under a signed
						engagement agreement, we will necessarily process information you
						make available to us in order to perform the services. This
						typically includes administrative access to your Shopify store, CRM,
						email and SMS platforms, ad accounts, analytics, accounting system,
						and similar systems. The categories of personal information involved
						may include:
					</p>
					<ul className="ml-6 list-disc space-y-1.5">
						<li>Employee, contractor, and stakeholder contact information you provide to us;</li>
						<li>End-customer records held inside your platforms (for example, order records, lead inquiries, customer service notes);</li>
						<li>Business operating data, financial data, and performance metrics.</li>
					</ul>
					<p>
						When we process information of this kind on behalf of a client, we
						act as a service provider, processor, or business contractor
						(depending on the applicable law). Our handling of that information
						is governed by the executed engagement documents — most commonly the
						Master Service Agreement, Application License Agreement, and
						applicable Statement of Work — which control over any conflicting
						language in this Policy with respect to that information.
					</p>
				</Section>

				<Section id="how-we-use" num="4" title="How We Use Information">
					<p>
						We use the information described in Section 3 for the following
						purposes:
					</p>
					<ul className="ml-6 list-disc space-y-2">
						<li>
							<strong>Respond to inquiries.</strong> To evaluate fit, schedule
							discovery calls, send Discovery proposals, and follow up on
							conversations you initiate.
						</li>
						<li>
							<strong>Deliver professional services.</strong> To perform the
							work scoped in an executed engagement agreement, including
							diagnostics, infrastructure design, application licensing, and
							ongoing services.
						</li>
						<li>
							<strong>Operate the business.</strong> To send invoices,
							administer agreements, process payments, maintain records, and
							manage client relationships.
						</li>
						<li>
							<strong>Improve the Website and our work product.</strong> To
							analyze how the Website is used, debug issues, prevent abuse, and
							refine our positioning, content, and case studies.
						</li>
						<li>
							<strong>Communicate.</strong> To send transactional emails,
							scheduling messages, engagement updates, and — only with
							appropriate consent or a clear business relationship — occasional
							emails relating to F&amp;L&rsquo;s work.
						</li>
						<li>
							<strong>Comply with law and protect rights.</strong> To meet
							legal, regulatory, tax, audit, and recordkeeping obligations; to
							enforce our agreements; and to defend against claims.
						</li>
						<li>
							<strong>Marketing materials, case studies, and content.</strong>{' '}
							Subject to the marketing and case study rights granted in our
							engagement agreements (which generally cover business-level
							information, anonymized operating data, performance metrics,
							screenshots, and recreated visualizations), and subject to the
							confidentiality carve-outs in those agreements (which protect
							end-customer names, end-customer personally identifiable
							information, and similar sensitive items), we may use information
							about a client engagement in case studies, articles, and other
							promotional content.
						</li>
					</ul>
					<p>
						We do not use the personal information of Website visitors or
						prospects for purposes materially different from those described
						above without a fresh, lawful basis to do so.
					</p>
				</Section>

				<Section id="legal-bases" num="5" title="Legal Bases for Processing (EEA, UK, Switzerland)">
					<p>
						Where the EU/UK General Data Protection Regulation or the Swiss
						Federal Act on Data Protection applies, we rely on one or more of
						the following legal bases:
					</p>
					<ul className="ml-6 list-disc space-y-2">
						<li>
							<strong>Performance of a contract</strong> or steps taken at your
							request prior to entering into a contract (for example, responding
							to a consultation request, scoping an engagement, performing
							services under an executed agreement);
						</li>
						<li>
							<strong>Legitimate interests</strong> in operating, securing, and
							improving the Website and our business, in marketing F&amp;L&rsquo;s
							services to business audiences, and in defending or asserting legal
							rights, where those interests are not overridden by your rights and
							freedoms;
						</li>
						<li>
							<strong>Consent</strong>, where we ask for it (for example, for
							non-essential cookies in jurisdictions that require opt-in, or for
							inclusion in a specific marketing list);
						</li>
						<li>
							<strong>Legal obligation</strong>, where processing is required by
							applicable law.
						</li>
					</ul>
				</Section>

				<Section id="how-we-share" num="6" title="How We Share Information">
					<p>
						We do not sell personal information, and we do not share personal
						information with third parties for cross-context behavioral
						advertising. We disclose personal information only as described
						below.
					</p>

					<SubHeading>6.1 Service Providers</SubHeading>
					<p>
						We share information with vendors and contractors who help us
						operate the Website and the business, under written terms requiring
						confidentiality and appropriate use. Categories include:
					</p>
					<ul className="ml-6 list-disc space-y-1.5">
						<li><strong>Website hosting, content delivery, and infrastructure</strong> (for example, our Website host and CDN);</li>
						<li><strong>Content management</strong> (for example, our headless CMS provider for Website content);</li>
						<li><strong>Customer relationship management</strong> (for example, the CRM we use to track prospects, accounts, and engagements);</li>
						<li><strong>Email, calendaring, and document collaboration</strong>;</li>
						<li><strong>Electronic signature and contract administration</strong>;</li>
						<li><strong>Telephony, call tracking, and meeting recording</strong>, where used;</li>
						<li><strong>Analytics</strong>;</li>
						<li><strong>Advertising measurement</strong> (for example, the advertising platforms we use to promote our own services, which receive conversion information under terms restricting them to processing it on our behalf);</li>
						<li><strong>Accounting, payment processing, and banking</strong>;</li>
						<li><strong>File storage and backup</strong>;</li>
						<li><strong>Counsel, accountants, and other professional advisors</strong>, where engaged.</li>
					</ul>
					<p>
						We use commercially reasonable efforts to engage providers that
						handle information securely and lawfully. We do not authorize
						providers to use information about our visitors or clients for their
						own marketing.
					</p>

					<SubHeading>6.2 With Your Direction or Authorization</SubHeading>
					<p>
						We share information when you direct us to do so or when sharing is
						reasonably necessary to perform the engagement (for example, when we
						coordinate with a third-party developer, agency, or platform on your
						behalf with your knowledge).
					</p>

					<SubHeading>6.3 Legal, Safety, and Enforcement</SubHeading>
					<p>
						We may disclose information when we believe in good faith that
						disclosure is necessary to: comply with applicable law, regulation,
						legal process, or governmental request; enforce our agreements,
						terms, and policies; investigate, prevent, or address fraud,
						security, or technical issues; or protect the rights, property, or
						safety of Frame &amp; Ledger, our clients, our personnel, or the
						public.
					</p>

					<SubHeading>6.4 Business Transactions</SubHeading>
					<p>
						If Frame &amp; Ledger is involved in a merger, acquisition,
						reorganization, financing, sale of assets, or similar transaction,
						information may be transferred as part of that transaction, subject
						to customary confidentiality protections and the requirements of
						applicable law.
					</p>

					<SubHeading>6.5 Aggregated or De-Identified Information</SubHeading>
					<p>
						We may create and share aggregated, anonymized, or de-identified
						information that does not reasonably identify any individual — for
						example, benchmark statistics, operating insights, and
						conversion-lag patterns derived from the engagements we have
						performed. This information is not personal information and is not
						subject to this Policy.
					</p>
				</Section>

				<Section id="cookies" num="7" title="Cookies and Similar Technologies">
					<p>
						The Website uses cookies and similar technologies (pixels, local
						storage, server logs) to:
					</p>
					<ul className="ml-6 list-disc space-y-1.5">
						<li>Operate the Website and keep it secure;</li>
						<li>Remember preferences (for example, theme or language, where supported);</li>
						<li>Measure how the Website is used so we can improve it;</li>
						<li>Diagnose problems.</li>
					</ul>
					<p>
						You can configure your browser to refuse cookies or to alert you
						when cookies are being set. If you reject essential cookies, parts
						of the Website may not function correctly. Where applicable law
						requires it, we will request your consent before setting
						non-essential cookies.
					</p>
					<p>
						We do not use third-party advertising cookies for retargeting on the
						Website. When you reach the Website through one of our
						advertisements, the advertising platform adds an identifier to the
						link; we store that identifier in a first-party cookie so we can
						tell which advertisement led to an inquiry.
					</p>
				</Section>

				<Section id="retention" num="8" title="Data Retention">
					<p>
						We retain personal information for as long as reasonably necessary
						to fulfill the purposes described in this Policy, including to
						provide our services, comply with legal and tax obligations, resolve
						disputes, defend claims, and enforce our agreements.
					</p>
					<p>Specific retention periods depend on the context. In general:</p>
					<ul className="ml-6 list-disc space-y-2">
						<li>
							<strong>Inquiry submissions</strong> that do not lead to an
							engagement are retained for a reasonable period to permit
							follow-up and to preserve a record of the inquiry, then deleted or
							anonymized.
						</li>
						<li>
							<strong>Engagement records</strong> — including agreements,
							statements of work, deliverables, communications, and operating
							data — are retained throughout the engagement and for the period
							required to administer the engagement, meet legal and accounting
							obligations, and exercise the marketing and case study rights
							granted in our agreements.
						</li>
						<li>
							<strong>Server logs and analytics</strong> are retained for the
							period needed for security, debugging, and analytics, and then
							aggregated or deleted.
						</li>
					</ul>
				</Section>

				<Section id="security" num="9" title="Data Security">
					<p>
						We maintain administrative, technical, and physical safeguards
						designed to protect personal information from loss, misuse, and
						unauthorized access, disclosure, alteration, and destruction. These
						safeguards include access controls, encryption in transit, vendor
						diligence, and least-privilege principles for client system access.
					</p>
					<p>
						No system is perfectly secure. We do not guarantee that personal
						information will remain free from loss, compromise, or breach, and
						we expressly disclaim any such warranty to the extent permitted by
						applicable law. Without limiting the foregoing, Frame &amp; Ledger
						does not represent or warrant that our Website, services, or
						operations are compliant with any specific regulation, standard, or
						certification framework — including HIPAA, PCI-DSS, COPPA,
						GDPR-specific certifications, SOC 2, ISO 27001, ADA/WCAG, or
						industry-specific regimes — unless and except as expressly committed
						in a signed agreement with you that scopes and prices such
						commitments.
					</p>
				</Section>

				<Section id="your-rights" num="10" title="Your Rights and Choices">
					<p>
						Subject to applicable law and the verification process described
						below, you have the following rights with respect to your personal
						information.
					</p>

					<SubHeading>10.1 General Rights</SubHeading>
					<p>You may:</p>
					<ul className="ml-6 list-disc space-y-2">
						<li>
							<strong>Access</strong> the personal information we hold about
							you;
						</li>
						<li>
							<strong>Correct</strong> inaccurate or incomplete information;
						</li>
						<li>
							<strong>Delete</strong> personal information, subject to
							exceptions for information we are required or permitted to retain;
						</li>
						<li>
							<strong>Opt out</strong> of marketing communications by using the
							unsubscribe link in any marketing email or by emailing{' '}
							<a
								href="mailto:privacy@frameandledger.com"
								className="text-gold underline hover:no-underline"
							>
								privacy@frameandledger.com
							</a>
							;
						</li>
						<li>
							<strong>Withdraw consent</strong> where processing is based on
							consent (without affecting the lawfulness of prior processing).
						</li>
					</ul>

					<SubHeading>10.2 California Residents (CCPA / CPRA)</SubHeading>
					<p>
						If you are a California resident, you have additional rights under
						the California Consumer Privacy Act, as amended by the California
						Privacy Rights Act, including the right to know what categories and
						specific pieces of personal information we have collected, the
						sources, the purposes, and the categories of recipients; the right
						to delete; the right to correct; and the right to limit the use of
						sensitive personal information.
					</p>
					<p>
						We do not “sell” personal information and we do not “share” personal
						information for cross-context behavioral advertising as those terms
						are defined under California law. You will not be discriminated
						against for exercising your California privacy rights.
					</p>
					<p>
						To submit a request, email{' '}
						<a
							href="mailto:privacy@frameandledger.com"
							className="text-gold underline hover:no-underline"
						>
							privacy@frameandledger.com
						</a>
						. You may use an authorized agent to make a request on your behalf,
						in which case we will verify the agent&rsquo;s authority.
					</p>

					<SubHeading>10.3 EEA, UK, and Swiss Residents (GDPR / UK GDPR)</SubHeading>
					<p>
						If you are located in the European Economic Area, the United
						Kingdom, or Switzerland, you have the rights to access, rectify,
						erase, restrict, port, and object to the processing of your personal
						data, and the right to lodge a complaint with a supervisory
						authority in your jurisdiction. Where we rely on legitimate
						interests, you may object on grounds relating to your particular
						situation.
					</p>

					<SubHeading>10.4 Florida Residents (FDBR)</SubHeading>
					<p>
						If you are a Florida resident and meet the applicability thresholds
						of the Florida Digital Bill of Rights, you may have rights to
						confirm, access, correct, delete, obtain a portable copy of, and opt
						out of certain processing of your personal data, including the sale
						of sensitive data, targeted advertising, and certain profiling.
						Frame &amp; Ledger does not engage in the sale of sensitive personal
						data and does not conduct targeted advertising of the kind addressed
						by the FDBR.
					</p>

					<SubHeading>10.5 Other Jurisdictions</SubHeading>
					<p>
						If you reside in a jurisdiction not specifically named above that
						grants similar rights, we will honor those rights to the extent
						required by applicable law.
					</p>

					<SubHeading>10.6 Verification</SubHeading>
					<p>
						To protect your information, we will take reasonable steps to verify
						your identity before responding to a rights request. We may ask you
						to confirm information we already hold or to provide additional
						information sufficient to confirm the request is genuine. If we
						cannot verify the request, we may decline it and will explain why.
					</p>
				</Section>

				<Section id="do-not-track" num="11" title="Do Not Track">
					<p>
						Some browsers transmit a “Do Not Track” signal. There is no industry
						standard for how to interpret this signal, and the Website does not
						currently respond to Do Not Track signals. You can manage cookies
						through your browser settings as described in Section 7.
					</p>
				</Section>

				<Section id="transfers" num="12" title="International Transfers">
					<p>
						Frame &amp; Ledger is based in Florida, United States. If you access
						the Website or engage with us from outside the United States, you
						understand that your information will be transferred to, stored, and
						processed in the United States and in any other country where our
						service providers operate. Data protection laws in the United States
						may differ from those in your country.
					</p>
					<p>
						Where required by applicable law, we will implement appropriate
						safeguards for international transfers — for example, by relying on
						Standard Contractual Clauses, applicable adequacy decisions, or
						equivalent mechanisms.
					</p>
				</Section>

				<Section id="children" num="13" title="Children’s Privacy">
					<p>
						The Website is not directed to children, and we do not knowingly
						collect personal information from anyone under the age of eighteen.
						If we learn that we have collected information from a child in a
						manner inconsistent with applicable law, we will delete it.
					</p>
				</Section>

				<Section id="third-party" num="14" title="Third-Party Links and Embedded Content">
					<p>
						The Website may link to or embed content from third parties (for
						example, LinkedIn profiles, video platforms, or partner sites).
						Those third parties operate independently and have their own privacy
						practices. We are not responsible for the content or privacy
						practices of any third party. We encourage you to review the privacy
						notice of any third-party site you visit.
					</p>
				</Section>

				<Section id="changes" num="15" title="Changes to This Policy">
					<p>
						We may update this Policy from time to time. When we do, we will
						revise the “Last Updated” date at the top of the Policy and, where
						required by law or where changes are material, take additional steps
						to notify affected individuals. Your continued use of the Website
						after an update constitutes acceptance of the updated Policy.
					</p>
				</Section>

				<Section id="contact" num="16" title="Contact Us">
					<p>
						If you have questions about this Policy or wish to exercise a
						privacy right, contact us at:
					</p>
					<address className="not-italic">
						<strong>Frame &amp; Ledger LLC</strong>
						<br />
						Attn: Privacy
						<br />
						<a
							href="mailto:privacy@frameandledger.com"
							className="text-gold underline hover:no-underline"
						>
							privacy@frameandledger.com
						</a>
					</address>
				</Section>

				<p className="text-sm italic text-muted-foreground">
					Frame &amp; Ledger LLC is a Florida limited liability company. This
					Privacy Policy and any non-contractual obligations arising from or in
					connection with it are governed by the laws of the State of Florida,
					without regard to its conflict-of-laws principles, except where
					mandatory local law of your jurisdiction provides otherwise.
				</p>

				<p className="text-sm text-muted-foreground">
					See also our{' '}
					<Link to="/tos" className="text-gold underline hover:no-underline">
						Terms of Service
					</Link>
					.
				</p>

				<footer className="mt-12 border-t border-border pt-6 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
					<p>⬢ The Frame is set. The Ledger is clear.</p>
				</footer>
			</article>
		</main>
	)
}

function Section({
	id,
	num,
	title,
	children,
}: {
	id: string
	num: string
	title: string
	children: React.ReactNode
}) {
	return (
		<section id={id} className="scroll-mt-24 pt-8">
			<h2 className="flex items-baseline gap-3 font-display text-2xl font-medium tracking-tight text-foreground sm:text-3xl">
				<span className="text-gold">§ {num.padStart(2, '0')}</span>
				<span>{title}</span>
			</h2>
			<div className="mt-4 space-y-4">{children}</div>
		</section>
	)
}

function SubHeading({ children }: { children: React.ReactNode }) {
	return (
		<h3 className="mt-6 font-display text-lg font-medium tracking-tight text-foreground sm:text-xl">
			{children}
		</h3>
	)
}
